"use client";

/**
 * Quiet inline preview for a use-case card — the SHORT manifest variant.
 *
 * Autoplay rules (deliberately strict, see docs/website/VIDEO_INTEGRATION.md):
 *   - only when the short video is published;
 *   - always muted + playsInline, loop, no controls-required interaction;
 *   - only on hover-capable (desktop) pointers — mobile always gets the poster
 *     with the card's explicit link, never a hover behavior;
 *   - only while ≥60% of the card is in the viewport (IntersectionObserver);
 *   - never with `prefers-reduced-motion: reduce`;
 *   - never with Save-Data or a constrained connection (2g/slow-2g);
 *   - at most ONE preview plays at a time (module-level arbitration);
 *   - pauses when the tab is hidden or the card leaves the viewport;
 *   - a visible stop/start toggle so motion can always be stopped.
 *
 * When any rule blocks playback the component renders the static poster (or
 * nothing when no poster exists yet) — information never depends on motion.
 */

import { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { useT } from "@/i18n";
import { getMarketingVideo, isPlayable, type MarketingVideoId } from "@/lib/marketing-videos";

/** Only one preview may play at a time, across all cards. */
let activePreview: HTMLVideoElement | null = null;

function connectionAllows(): boolean {
  type NetInfo = { saveData?: boolean; effectiveType?: string };
  const conn = (navigator as Navigator & { connection?: NetInfo }).connection;
  if (!conn) return true;
  if (conn.saveData) return false;
  if (conn.effectiveType && /(^|-)2g$/.test(conn.effectiveType)) return false;
  return true;
}

export function VideoPreview({ id }: { id: MarketingVideoId }) {
  const t = useT();
  const video = getMarketingVideo(id);
  const ref = useRef<HTMLVideoElement | null>(null);
  const [eligible, setEligible] = useState(false);
  const [inView, setInView] = useState(false);
  const [userStopped, setUserStopped] = useState(false);
  // Whether the element is actually playing right now. When it is not (e.g.
  // another card's preview won the one-at-a-time arbitration, or the video
  // sits on its dark intro frame), we show the poster instead of a stale
  // paused frame — a card must never render as a black box.
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!isPlayable(video)) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const hoverable = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () =>
      setEligible(!reduced.matches && hoverable.matches && connectionAllows());
    update();
    reduced.addEventListener("change", update);
    hoverable.addEventListener("change", update);
    return () => {
      reduced.removeEventListener("change", update);
      hoverable.removeEventListener("change", update);
    };
  }, [video]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !eligible) return;
    const io = new IntersectionObserver((entries) => setInView(entries[0]?.isIntersecting ?? false), {
      threshold: 0.6,
    });
    io.observe(el);
    const onVisibility = () => {
      if (document.hidden) el.pause();
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    document.addEventListener("visibilitychange", onVisibility);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
    };
  }, [eligible]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (eligible && inView && !userStopped && !document.hidden) {
      if (activePreview && activePreview !== el) activePreview.pause();
      activePreview = el;
      void el.play().catch(() => {
        /* Autoplay refused by the browser — poster stays, nothing breaks. */
      });
    } else {
      el.pause();
      if (activePreview === el && !inView) activePreview = null;
    }
  }, [eligible, inView, userStopped]);

  if (!isPlayable(video)) return null;

  if (!eligible) {
    // Mobile / reduced-motion / constrained network: static poster only.
    return video.poster ? (
      <span className="block aspect-video w-full overflow-hidden rounded-lg border border-line-subtle">
        <picture>
          <source srcSet={video.poster.avif} type="image/avif" />
          <source srcSet={video.poster.webp} type="image/webp" />
          {/* Plain <img>: static, pre-optimized poster — see VideoPoster. */}
          <img
            src={video.poster.jpg}
            alt=""
            width={video.width}
            height={video.height}
            loading="lazy"
            decoding="async"
            className="block h-full w-full object-cover"
          />
        </picture>
      </span>
    ) : null;
  }

  return (
    <span className="relative block aspect-video w-full overflow-hidden rounded-lg border border-line-subtle">
      <video
        ref={ref}
        className="block h-full w-full object-cover"
        muted
        loop
        playsInline
        preload="none"
        poster={video.poster?.jpg}
        width={video.width}
        height={video.height}
        aria-hidden
        tabIndex={-1}
      >
        <source src={video.mp4} type="video/mp4" />
      </video>
      {/* Poster on top whenever the preview is not actually playing. */}
      {!playing && video.poster && (
        <picture aria-hidden className="pointer-events-none absolute inset-0">
          <source srcSet={video.poster.avif} type="image/avif" />
          <source srcSet={video.poster.webp} type="image/webp" />
          <img
            src={video.poster.jpg}
            alt=""
            width={video.width}
            height={video.height}
            loading="lazy"
            decoding="async"
            className="block h-full w-full object-cover"
          />
        </picture>
      )}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setUserStopped((s) => !s);
        }}
        aria-label={userStopped ? t("videoKit.previewStart") : t("videoKit.previewStop")}
        className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-md border border-line-subtle bg-bg-base/80 text-fg backdrop-blur transition hover:text-fg-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
      >
        {userStopped ? <Play size={14} /> : <Pause size={14} />}
      </button>
    </span>
  );
}
